import { FC } from 'react';
import { useMeasure } from 'react-use';

import Icon from 'atoms/Icons/Icon';
import color from 'atoms/colors/colors';

// Quarks
import Flex from 'quarks/Flex';
import type { FlexProps } from 'quarks/interpolations/flex';

import Dots from 'molecules/Pagination/Dots';

import TestimonialImageCard from 'components/Cards/TestimonialImageCard';
import ComponentHeading from 'components/Heading/Heading';

import useSwipeable from 'utils/useSwipeable';

// Types
import type { ContentfulComponentTestimonialSlider } from 'graphqlTypes';

interface TestimonialSliderProps extends FlexProps, ContentfulComponentTestimonialSlider {
  backgroundIsDark: boolean;
  testimonialHeading?: ContentfulComponentTestimonialSlider['heading'];
  cIndex: number;
}

const ComponentTestimonialSlider: FC<TestimonialSliderProps> = ({
  testimonialHeading,
  backgroundIsDark,
  testimonial,
  cIndex,
  ...props
}) => {
  const length = testimonial?.length || 0;
  const [activeIndex, setActive, cardContainerRef] = useSwipeable(length);
  const [ref, { width: cardWidth }] = useMeasure<HTMLDivElement>();

  return (
    <Flex flexDirection="column" gap="16px" {...props}>
      {testimonialHeading && (
        <Flex width="100%">
          <ComponentHeading backgroundIsDark={backgroundIsDark} {...testimonialHeading} textAlign="left" />
        </Flex>
      )}
      <Flex flexDirection="column" gap="24px">
        {testimonial && (
          <Flex
            gap="30px"
            justifyContent="flex-start"
            transform={`translateX(-${activeIndex * cardWidth + activeIndex * 30}px)`}
            transition="transform 200ms cubic-bezier(0.215, 0.610, 0.355, 1.000)"
            minWidth={`calc(100% * ${length} + (30px * ${length - 1}))`}
            ref={cardContainerRef}
          >
            {testimonial.map(
              (item, idx) =>
                item?.themeGallery && (
                  <Flex
                    key={item?.id}
                    ref={ref}
                    className={`tabbed-image-item-${cIndex}`}
                    width="100%"
                    height="100%"
                    maxHeight="fit-content"
                    maxWidth="100%"
                    overflowX="hidden"
                    overflowY="hidden"
                    opacity={activeIndex === idx ? 1 : '0.3'}
                    transition="200ms ease-in-out"
                  >
                    <TestimonialImageCard
                      backgroundIsDark={backgroundIsDark}
                      {...item}
                      isActive={activeIndex === idx}
                    />
                  </Flex>
                ),
            )}
          </Flex>
        )}
        {testimonial && testimonial.length > 1 && (
          <Flex
            width="100%"
            alignItems="center"
            justifyContent="space-between"
            gap="16px"
            flexWrap="wrap"
            marginTop={8}
          >
            <Flex gap="12px">
              <Flex
                borderRadius="6px"
                backgroundColor="navy-700"
                width="36px"
                height="36px"
                alignItems="center"
                justifyContent="center"
                cursor="pointer"
                border="1px solid"
                borderColor="navy-700"
                onClick={() => (activeIndex === 0 ? setActive(length - 1) : setActive(activeIndex - 1))}
              >
                <Icon id="arrow-left" fill={backgroundIsDark ? color.common.white : color.navy[900]} size={16} />
              </Flex>
              <Flex
                borderRadius="6px"
                backgroundColor="navy-700"
                width="36px"
                height="36px"
                alignItems="center"
                justifyContent="center"
                cursor="pointer"
                border="1px solid"
                borderColor="navy-700"
                onClick={() => (activeIndex === length - 1 ? setActive(0) : setActive(activeIndex + 1))}
              >
                <Icon id="arrow-right" fill={backgroundIsDark ? color.common.white : color.navy[900]} size={16} />
              </Flex>
            </Flex>
            <Flex gap="16px">
              {[...Array(length).keys()]?.map((_, index: number) =>
                index === activeIndex ? (
                  <Dots isActive activeColor="green-300" onClick={() => setActive(index)} key={`active-dot-${index}`} />
                ) : (
                  <Dots
                    color="navy-300"
                    onKeyDown={e => e.key === 'Enter' && setActive(index)}
                    onClick={() => setActive(index)}
                    key={`inactive-dot-${index}`}
                  />
                ),
              )}
            </Flex>
          </Flex>
        )}
        {testimonial && (
          <Flex
            gap="30px"
            justifyContent="flex-start"
            transform={`translateX(-${activeIndex * cardWidth + activeIndex * 30}px)`}
            transition="transform 200ms cubic-bezier(0.215, 0.610, 0.355, 1.000)"
            minWidth={`calc(100% * ${length} + (30px * ${length - 1}))`}
            ref={cardContainerRef}
          >
            {testimonial.map(
              (item, idx) =>
                item?.themeGallery && (
                  <Flex
                    key={item?.id}
                    ref={ref}
                    className={`tabbed-image-item-${cIndex}`}
                    width="100%"
                    height="100%"
                    maxHeight="fit-content"
                    maxWidth="100%"
                    overflowX="hidden"
                    overflowY="hidden"
                    opacity={activeIndex === idx ? 1 : '0.3'}
                    transition="200ms ease-in-out"
                  >
                    <TestimonialImageCard
                      backgroundIsDark={backgroundIsDark}
                      {...item}
                      isActive={activeIndex === idx}
                      isDesc
                    />
                  </Flex>
                ),
            )}
          </Flex>
        )}
      </Flex>
    </Flex>
  );
};

export default ComponentTestimonialSlider;
